import React, { useState, createContext, useContext, useMemo } from 'react'
import useSWR from 'swr'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import Loader from '@/components/ui/Loader'
import EmptyDatas from 'components/ui/EmptyDatas'
import Table from 'components/ui/my-data-table'
import { arrayMove } from '@dnd-kit/sortable'
import SortableDataTable from 'Editor/RightPanel/Layers/sortable-data-table'
import {
  columnCover,
  columnPreview,
  columnName,
  columnPainterFrames,
  columnLevel,
  columnTags,
  columnAvatars,
} from 'components/template-data-table-column'
import DisplayUpdateTime from 'ui/DisplayUpdateTime'
import useSWRMutation from 'swr/mutation'
import { getFetcher, postFetcher, putFetcher } from 'api/swr'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  ReloadIcon,
  PlusIcon,
  DotsHorizontalIcon,
  ArrowUpIcon,
  TrashIcon,
  ImageIcon,
  Pencil1Icon,
  EyeNoneIcon,
  EyeOpenIcon,
} from '@radix-ui/react-icons'
import { useToast } from '@/components/ui/use-toast'
import RedisKeyHint from './RedisKeyHint'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  OutfitListConfigDialog,
  OutfitListCoverCell,
  OutfitListBadgeCell,
  OutfitListButtonCell,
} from './OutfitListConfigCell'

// 创建 Context 来传递 mutate 函数
const MutateContext = createContext()

export const useMutate = () => {
  const mutate = useContext(MutateContext)
  if (!mutate) {
    console.warn('useMutate must be used within a MutateProvider')
    return () => {}
  }
  return mutate
}

const LIST_LABELS = {
  spine: 'Spine',
  christmas: 'Christmas',
  outfit: 'Templates with Sets',
}

const outfitColumnName = {
  header: 'Name',
  accessorKey: 'name',
  cell: ({ row }) => (
    <>
      <div className="flex items-center gap-2">
        <div className="fw-bold">{row.original.name}</div>
        {row.original.inOnlines === false ? (
          <Badge
            variant="outline"
            className="border-amber-300 bg-amber-50 text-[10px] font-medium text-amber-700"
          >
            未上线
          </Badge>
        ) : null}
      </div>
      <div className="text-black/90 text-xs">{row.original.id}</div>
      <p className="text-ellipsis w-56 text-black/60 text-xs line-clamp-2">
        {row.original.intro}
      </p>
    </>
  ),
}

const TemplateLists = ({ listType = 'christmas' }) => {
  const listLabel =
    LIST_LABELS[listType] || listType.charAt(0).toUpperCase() + listType.slice(1)
  const canDragSort = listType === 'outfit'

  const { data, isLoading, mutate } = useSWR([
    `/template-lists/${listType}`,
  ])
  const { toast } = useToast()
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false)
  const [isOnlineConfirmOpen, setIsOnlineConfirmOpen] = useState(false)
  const [showWhenOnline, setShowWhenOnline] = useState(true)
  const [isChecking, setIsChecking] = useState(false)
  const [newTemplateId, setNewTemplateId] = useState('')

  const { trigger: addTemplate, isMutating: isAdding } = useSWRMutation(
    `/template-lists/${listType}`,
    postFetcher,
  )

  const { trigger: syncTemplates, isMutating: isSyncing } = useSWRMutation(
    '/template-lists/templateList/sync',
    postFetcher,
  )

  const finishAddTemplate = async (hidden) => {
    const payload = { templateId: newTemplateId.trim() }
    if (typeof hidden === 'boolean') {
      payload.hidden = hidden
    }
    const result = await addTemplate(payload)
    setNewTemplateId('')
    setIsAddDialogOpen(false)
    setIsOnlineConfirmOpen(false)
    setShowWhenOnline(true)
    mutate()
    toast({
      title: 'Success',
      description:
        listType === 'outfit' && result?.hidden === false
          ? '已加入列表，待上线后客户端自动展示'
          : '已加入列表，默认隐藏，客户端不展示',
      className: 'bg-green-500 text-white',
    })
  }

  const handleAddTemplate = async () => {
    if (!newTemplateId.trim() || isAdding || isChecking) {
      return
    }
    if (listType !== 'outfit') {
      try {
        await finishAddTemplate()
      } catch (error) {
        console.error('Failed to add template:', error)
        toast({
          title: 'Error',
          description:
            'Failed to add template: ' + (error.message || 'Unknown Error'),
          variant: 'destructive',
        })
      }
      return
    }
    const tid = newTemplateId.trim()
    setIsChecking(true)
    try {
      try {
        await getFetcher(`/frames/${encodeURIComponent(tid)}`)
      } catch {
        setShowWhenOnline(true)
        setIsOnlineConfirmOpen(true)
        return
      }
      await finishAddTemplate(true)
    } catch (error) {
      console.error('Failed to add template:', error)
      toast({
        title: 'Error',
        description:
          'Failed to add template: ' + (error.message || 'Unknown Error'),
        variant: 'destructive',
      })
    } finally {
      setIsChecking(false)
    }
  }

  const handleConfirmUnpublishedAdd = async () => {
    if (isAdding) {
      return
    }
    try {
      await finishAddTemplate(!showWhenOnline)
    } catch (error) {
      console.error('Failed to add template:', error)
      toast({
        title: 'Error',
        description:
          'Failed to add template: ' + (error.message || 'Unknown Error'),
        variant: 'destructive',
      })
    }
  }

  const handleSyncTemplates = async () => {
    if (isSyncing) {
      return
    }
    try {
      const result = await syncTemplates({ tag: listType })
      setIsSyncDialogOpen(false)
      mutate()
      if (result?.success) {
        console.log(`Successfully synced ${result.count} templates`)
      }
    } catch (error) {
      console.error('Failed to sync templates:', error)
    }
  }

  const columns = useMemo(
    () => [
      ...(canDragSort
        ? [
            {
              id: 'drag-handle',
              header: '',
            },
          ]
        : []),
      {
        header: '#',
        cell: ({ row }) => row.index + 1,
      },
      ...(listType === 'outfit'
        ? [
            {
              header: 'List Cover',
              cell: ({ row }) => <OutfitListCoverCell row={row} />,
            },
          ]
        : []),
      columnCover,
      columnPreview,
      listType === 'outfit' ? outfitColumnName : columnName,
      columnPainterFrames,
      columnLevel,
      listType === 'outfit'
        ? {
            ...columnTags,
            cell: (ctx) => (
              <div className="w-[40rem] max-w-[40rem]">
                {columnTags.cell(ctx)}
              </div>
            ),
          }
        : columnTags,
      ...(listType === 'outfit'
        ? [
            {
              header: '标签',
              cell: ({ row }) => <OutfitListBadgeCell row={row} />,
            },
            {
              header: '按钮文案',
              cell: ({ row }) => <OutfitListButtonCell row={row} />,
            },
          ]
        : []),
      columnAvatars,
      {
        header: '模板状态',
        id: 'onlineState',
        cell: ({ row }) => {
          const d = row.original
          if (listType === 'outfit' && d.inOnlines === false) {
            return (
              <span
                className="font-medium text-amber-600"
                title="不在 Onlines / frames 中，客户端暂不展示"
              >
                未上线
              </span>
            )
          }
          let online
          if (d.state !== undefined && d.state !== null) {
            online = d.state === 401
          } else {
            const cl = d.contentLangs
            online = Array.isArray(cl) && cl.length > 0
          }
          return (
            <span
              className={
                online
                  ? 'text-green-600 font-medium'
                  : 'text-muted-foreground'
              }
              title={
                d.state !== undefined && d.state !== null
                  ? 'state === 401 为上线，否则为下线'
                  : '无 state 时：contentLangs 非空为上线，否则为下线'
              }
            >
              {online ? '上线' : '下线'}
            </span>
          )
        },
      },
      {
        header: '列表状态',
        id: 'listHidden',
        cell: ({ row }) => {
          const hidden = Boolean(row.original.listHidden)
          const pendingOnline =
            !hidden && row.original.inOnlines === false
          return (
            <span
              className={
                hidden
                  ? 'font-medium text-red-500'
                  : pendingOnline
                    ? 'font-medium text-amber-600'
                    : 'font-medium text-green-600'
              }
              title={
                hidden
                  ? '已隐藏，客户端列表不展示'
                  : pendingOnline
                    ? '未上线，待进入 Onlines 且国家匹配后客户端展示'
                    : '展示中，客户端列表会返回'
              }
            >
              {hidden ? '隐藏' : pendingOnline ? '待上线' : '展示'}
            </span>
          )
        },
      },
      {
        header: 'CreateTime',
        accessorKey: 'createTime',
        cell: ({ row }) => (
          <DisplayUpdateTime updateTime={row.original.createTime} />
        ),
      },
      {
        header: 'Actions',
        cell: ({ row }) => (
          <DataTableRowActions row={row} listType={listType} />
        ),
      },
    ],
    [listType, canDragSort],
  )

  const table = useReactTable({
    data: data || [],
    columns,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleDragEnd = async ({ active, over }) => {
    if (!canDragSort || !over || active.id === over.id || !data?.length) {
      return
    }
    const oldIndex = data.findIndex((item) => item.id === active.id)
    const newIndex = data.findIndex((item) => item.id === over.id)
    if (oldIndex < 0 || newIndex < 0) {
      return
    }
    const next = arrayMove(data, oldIndex, newIndex)
    await mutate(next, { revalidate: false })
    try {
      await putFetcher(`/template-lists/${listType}/reorder`, {
        arg: { ids: next.map((item) => item.id) },
      })
    } catch (error) {
      await mutate()
      toast({
        title: 'Error',
        description:
          'Failed to save order: ' + (error.message || 'Unknown Error'),
        variant: 'destructive',
      })
    }
  }

  if (isLoading && !data) return <Loader />
  if (!data) return <EmptyDatas />

  return (
    <MutateContext.Provider value={mutate}>
      <div className="flex-shrink-0 mb-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => mutate()}>
            Refetch
          </Button>
          <RedisKeyHint
            keys={[
              `template:list:${listType}`,
              `template:list:${listType}:hidden`,
            ]}
            intersect={false}
          />
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="default">
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Template
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add {listLabel} Template</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="templateId">Template ID</Label>
                  <Input
                    id="templateId"
                    placeholder="Enter template ID"
                    value={newTemplateId}
                    onChange={(e) => setNewTemplateId(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddTemplate()
                      }
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    {listType === 'outfit'
                      ? '没有套装的模板不能加入。有套装时默认用套装 1 作为列表主图。Onlines 里的模板加入后默认隐藏。未上线的模板加入时会再确认：上线后是否直接展示。'
                      : '加入后默认为隐藏，客户端列表不展示，可在 Actions 里显示。'}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAddDialogOpen(false)
                    setNewTemplateId('')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddTemplate}
                  disabled={isAdding || isChecking}
                >
                  {isAdding || isChecking ? 'Adding...' : 'Add'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog
            open={isOnlineConfirmOpen}
            onOpenChange={(open) => {
              setIsOnlineConfirmOpen(open)
              if (!open) {
                setShowWhenOnline(true)
              }
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>未上线模板</DialogTitle>
                <DialogDescription>
                  该模板还不在 Onlines。加入列表后，上线时是否直接在客户端展示？
                </DialogDescription>
              </DialogHeader>
              <label
                htmlFor="showWhenOnline"
                className="flex items-start gap-2 py-2 text-sm"
              >
                <Checkbox
                  id="showWhenOnline"
                  checked={showWhenOnline}
                  onCheckedChange={(checked) =>
                    setShowWhenOnline(checked === true)
                  }
                  className="mt-0.5"
                />
                <span>
                  上线后直接展示
                  <span className="mt-1 block text-xs text-muted-foreground">
                    取消勾选则加入后隐藏，上线后仍需手动点显示。
                  </span>
                </span>
              </label>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsOnlineConfirmOpen(false)
                    setShowWhenOnline(true)
                  }}
                  disabled={isAdding}
                >
                  取消
                </Button>
                <Button
                  onClick={handleConfirmUnpublishedAdd}
                  disabled={isAdding}
                >
                  {isAdding ? 'Adding...' : '加入'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={isSyncDialogOpen} onOpenChange={setIsSyncDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>确认同步</DialogTitle>
                <DialogDescription>
                  {listType === 'outfit'
                    ? '扫描 defaultSet=1 且仍 online 的模板，把列表里还没有的追加进去。已有成员（含未上线）和手动顺序会保留。'
                    : `确定要同步 ${listLabel} 列表吗？将清空当前 Redis 列表并按服务端规则重新写入。`}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsSyncDialogOpen(false)}
                  disabled={isSyncing}
                >
                  取消
                </Button>
                <Button
                  onClick={handleSyncTemplates}
                  disabled={isSyncing}
                  variant="default"
                >
                  {isSyncing ? '同步中...' : '确认同步'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button
            variant="outline"
            onClick={() => setIsSyncDialogOpen(true)}
            disabled={isSyncing}
            className="gap-2"
          >
            <ReloadIcon className="w-4 h-4" />
            同步 {listLabel}
          </Button>
        </div>
      </div>
      <div className="flex-1 h-[calc(100vh-254px)]">
        <Card className="h-full flex flex-col">
          <CardContent className="flex-1 overflow-hidden pl-4 pr-0 pb-0">
            <div className="h-full overflow-auto">
              {canDragSort ? (
                <SortableDataTable table={table} onDragEnd={handleDragEnd} />
              ) : (
                <Table table={table} />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </MutateContext.Provider>
  )
}

const DataTableRowActions = ({ row, listType }) => {
  const data = row.original
  const mutate = useMutate()
  const { toast } = useToast()
  const [configOpen, setConfigOpen] = useState(false)

  const { trigger: pinTemplate, isMutating: isPinning } = useSWRMutation(
    `/template-lists/${listType}/pin`,
    putFetcher,
  )

  const { trigger: removeTemplate, isMutating: isRemoving } = useSWRMutation(
    `/template-lists/${listType}/remove`,
    postFetcher,
  )

  const { trigger: setListHidden, isMutating: isHiding } = useSWRMutation(
    `/template-lists/${listType}/hidden`,
    putFetcher,
  )

  const listHidden = Boolean(data.listHidden)

  const handlePinToTop = async (e) => {
    e.preventDefault()
    try {
      await pinTemplate({ templateId: data.id })
      toast({
        title: 'Success',
        description: 'Template pinned to top successfully',
        className: 'bg-green-500 text-white',
      })
      if (mutate && typeof mutate === 'function') {
        mutate()
      }
    } catch (error) {
      toast({
        title: 'Error',
        description:
          'Failed to pin template: ' + (error.message || 'Unknown Error'),
        variant: 'destructive',
      })
    }
  }

  const handleRemove = async (e) => {
    e.preventDefault()
    try {
      await removeTemplate({ templateId: data.id })
      toast({
        title: 'Success',
        description: 'Template removed successfully',
        className: 'bg-green-500 text-white',
      })
      if (mutate && typeof mutate === 'function') {
        mutate()
      }
    } catch (error) {
      console.error(error)
      toast({
        title: 'Error',
        description:
          'Failed to remove template: ' + (error.message || 'Unknown Error'),
        variant: 'destructive',
      })
    }
  }

  const handleToggleListHidden = async (e) => {
    e.preventDefault()
    const nextHidden = !listHidden
    try {
      await setListHidden({ templateId: data.id, hidden: nextHidden })
      toast({
        title: 'Success',
        description: nextHidden ? '已从客户端列表隐藏' : '已在客户端列表展示',
        className: 'bg-green-500 text-white',
      })
      if (mutate && typeof mutate === 'function') {
        mutate()
      }
    } catch (error) {
      toast({
        title: 'Error',
        description:
          'Failed to update list visibility: ' +
          (error.message || 'Unknown Error'),
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          >
            <DotsHorizontalIcon className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[180px]">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          {listType === 'outfit' ? (
            <>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault()
                  setConfigOpen(true)
                }}
              >
                <ImageIcon className="mr-2 h-4 w-4" />
                <span>配置展示</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() =>
                  window.open(
                    `/editor/${data.id}?tab=defaultSet`,
                    '_blank',
                  )
                }
              >
                <Pencil1Icon className="mr-2 h-4 w-4" />
                <span>Go to editor</span>
              </DropdownMenuItem>
            </>
          ) : (
            <DropdownMenuItem onSelect={handlePinToTop} disabled={isPinning}>
              <ArrowUpIcon className="mr-2 h-4 w-4" />
              <span>Pin to Top{isPinning ? '...' : null}</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onSelect={handleToggleListHidden}
            disabled={isHiding}
            className={
              listHidden ? undefined : 'text-red-500 focus:text-red-500'
            }
          >
            {listHidden ? (
              <EyeOpenIcon className="mr-2 h-4 w-4" />
            ) : (
              <EyeNoneIcon className="mr-2 h-4 w-4" />
            )}
            <span>
              {listHidden ? '显示' : '隐藏'}
              {isHiding ? '...' : null}
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={handleRemove}
            disabled={isRemoving}
            className="text-red-500"
          >
            <TrashIcon className="mr-2 h-4 w-4" />
            <span>Remove{isRemoving ? '...' : null}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {listType === 'outfit' ? (
        <OutfitListConfigDialog
          open={configOpen}
          onOpenChange={setConfigOpen}
          row={row}
          mutate={mutate}
        />
      ) : null}
    </>
  )
}

export default TemplateLists
